import { Add } from '@mui/icons-material';
import { Button } from '@mui/material';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { AgGridReact } from 'ag-grid-react';
import React, { useCallback, useEffect, useRef, useState } from "react";
import { connect, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Card, CardBody, Col, Input, Row } from "reactstrap";
import RequireUserType from '../../routes/middleware/requireUserType';
import { addToFavouriteReq, getPurchaseRequisitionListReq } from '../../service/purchaseRequisitionService';
import { setBreadcrumbItems } from "../../store/Breadcrumb/actions";
import { changePreloader } from "../../store/actions";
import { USER_TYPES_ENUM } from '../../utility/constants';
import { formatDate } from '../../utility/formatDate';
import DropdownMenuBtn from '../Orders/DropdownMenuBtn';
import OrderStatusRenderer from '../Orders/OrderStatusRenderer';

const AllRequisition = (props) => {
  document.title = "All Purchase Requisition";
  let dispatch = useDispatch();
  let navigate = useNavigate();

  const autoSizeStrategy = {
    type: 'fitGridWidth'
  };
  const paginationPageSizeSelector = [25, 50, 100];

  const [rowData, setRowData] = useState([]);
  const [paginationPageSize, setPaginationPageSize] = useState(25);
  const [page, setPage] = useState(1);
  const [searchValue, setSearchValue] = useState();
  const [inputValue, setInputValue] = useState('');
  const [sortData, setSortData] = useState({
    key: 'createdAt',
    order: "desc"
  });

  const timerRef = useRef(null);

  const redirectToViewPage = (id) => {
    let path = `/purchase-requisitions/${id}`;
    setTimeout(() => {
      navigate(path, id);
    }, 300);
  };

  const handleViewClick = (id) => {
    redirectToViewPage(id.purchaseRequisitionId);
  }

  useEffect(() => {
    props.setBreadcrumbItems('Purchase Requisitions', breadcrumbItems);
  }, []);

  useEffect(() => {
    let body = {
      page: page,
      limit: paginationPageSize,
    }
    if (searchValue) {
      body.search = searchValue
    }
    if (sortData) {
      body.sort = sortData
    }
    getListOfRowData(body);
  }, [page, paginationPageSize, searchValue, sortData]);

  const getListOfRowData = useCallback(async (body) => {
    dispatch(changePreloader(true));
    try {
      const response = await getPurchaseRequisitionListReq(body);

      if (!response?.data || !Array.isArray(response.data)) return;

      const res = response.data.map(requisition => ({
        purchaseRequisitionId: requisition._id,
        purchaseRequisitionNumber: requisition.purchaseRequisitionNumber || "-",
        purchaseOrderNumber: requisition.purchaseOrderNumber || "-",
        client_name: requisition.clientId?.name || "-",
        createdAt: formatDate(requisition.createdAt),
        branch_name: requisition.billing.branchName || "-",
        status: requisition.PRStatus,
      }));

      setRowData(res);
    } catch (error) {
      console.error("Error fetching purchase requisitions:", error);
    } finally {
      dispatch(changePreloader(false));
    }
  }, []);

  const redirectToEditPage = (id) => {
    let path = `/purchase-requisitions/${id.purchaseRequisitionId}/edit`;
    setTimeout(() => {
      navigate(path);
    }, 300);
  }

  const handleDeleteResponse = (response) => {
    if (response.success === true) {
      notify("Success", response.message);
    } else {
      notify("Error", response.message);
    }
    getListOfRowData();
  }

  const redirectToCopyPage = (id) => {
    let path = `/purchase-requisitions/${id.purchaseRequisitionId}/copy`;
    setTimeout(() => {
      navigate(path);
    }, 300);
  }

  const handleEditClick = (id) => {
    redirectToEditPage(id);
  }

  const handleCopyClick = (id) => {
    redirectToCopyPage(id);
  }


  const handleAddToFavourite = async (id) => {
    const response = await addToFavouriteReq(id.purchaseRequisitionId);
    if (response.success === true) {
      notify("Success", 'Order added to favourite successfully');
      getListOfRowData();
    } else {
      notify("Error", 'Failed to add order to favourite');
    }
  }

  const onPaginationChanged = useCallback((event) => {
    // Workaround for bug in events order
    const pageSize = gridRef.current.api.paginationGetPageSize();

    if (pageSize !== paginationPageSize) {
      setPaginationPageSize(pageSize);
    }
    const newPage = gridRef.current.api.paginationGetCurrentPage() + 1;

    if (page !== newPage) {
      setPage(newPage);
    }
  }, [page, paginationPageSize]);


  const handleInputChange = (e) => {
    setInputValue(e.target.value?.trim());
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      setRowData([]);
      setPage(1);
      setSearchValue(e.target.value?.trim());
    }, 2000);
  };

  const gridRef = useRef();

  const breadcrumbItems = [
    { title: "Dashboard", link: "/dashboard" },
    { title: "Purchase Requisitions", link: "#" },
  ];

  const columnDefs = [
    {
      headerName: "PR No.",
      field: "purchaseRequisitionNumber",
      suppressMenu: true,
      minWidth: 150,
      sortable: true,
      cellRenderer: (params) => {
        if (!params.data) return "-";
        const prId = params.data?.purchaseRequisitionId;
        const number = params.data.purchaseRequisitionNumber;

        return (
          <span
            style={{
              cursor: number !== '-' ? "pointer" : "default",
              color: number !== '-' ? "#007bff" : "inherit",
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (number !== '-') navigate(`/purchase-requisitions/${prId}`);
            }}
          >
            {number || "-"}
          </span>
        );
      },
    },
    {
      headerName: "PO No.",
      field: "purchaseOrderNumber",
      suppressMenu: true,
      minWidth: 120,
      cellRenderer: (params) => {
        if (!params.data) return "-";
        const poNumber = params.value;
        const prId = params.data?.purchaseRequisitionId;

        if (!poNumber) return "-";

        return (
          <span
            style={{
              cursor: poNumber !== '-' && "pointer",
              color: poNumber !== '-' && "#007bff",
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (poNumber !== '-') navigate(`/purchase-orders/${prId}`);
            }}
          >
            {poNumber}
          </span>
        );
      },
    },
    {
      headerName: 'Branch',
      field: 'branch_name',
      minWidth: 250,
      sortable: true,
      comparator: () => false,
      floatingFilterComponentParams: { suppressFilterButton: true },
      tooltipValueGetter: (p) => p.value,
      headerTooltip: "Branch",
      wrapText: true,
      autoHeight: true,
      cellStyle: {
        textAlign: 'left',
      },
      cellRenderer: (params) => (
        <span>{params.value}</span>
      ),
    },
    {
      headerName: "Created Date", field: "createdAt", suppressMenu: true,
      floatingFilterComponentParams: { suppressFilterButton: true },
      tooltipValueGetter: (p) => p.value, headerTooltip: "Created Date",
      sortable: true, minWidth: 150,
      comparator: () => false,
      sort: sortData.key === "createdAt" && sortData.order
    },
    {
      headerName: "Status",
      field: "status",
      suppressMenu: true,
      minWidth: 150,
      sortable: true,

      cellStyle: {
        textAlign: "left",
        display: "flex",
        justifyContent: "flex-start",
      },

      cellRenderer: (params) => (
        <OrderStatusRenderer value={params.value} type="pr" />
      ),
    },
    {
      headerName: "Action", field: "action", sortable: false, width: 100,
      cellClass: "actions-button-cell",
      cellRenderer: DropdownMenuBtn,
      cellRendererParams: {
        handleResponse: handleDeleteResponse,
        handleEditClick: handleEditClick,
        handleViewClick: handleViewClick,
        handleCopyClick: handleCopyClick,
        handleAddToFavourite: handleAddToFavourite,
      }, suppressMenu: true, floatingFilterComponentParams: { suppressFilterButton: true },
      tooltipValueGetter: (p) => p.value, headerTooltip: "Actions",
    }
  ];

  const notify = (type, message) => {
    if (type === "Error") {
      toast.error(message, {
        position: "top-center",
        theme: "colored",
      })
    } else {
      toast.success(message, {
        position: "top-center",
        theme: "colored",
      })
    }
  }

  const handleSortChange = (e) => {
    const columns = gridRef.current.api.getColumnState();
    const ele = columns.find(
      (ele) => ele.sort === "asc" || ele.sort === "desc"
    );

    if (ele) {
      setRowData([]);
      setPage(1);
      setSortData({
        key: ele.colId,
        order: ele.sort,
      });
    }
  };

  return (
    <>
      <div className="all-items">
        <Row>
          <Col className="col-12">
            <Card>
              <CardBody>
                <div className="button-section">
                  <div className="button-right-section">
                    <div className="invoice-search-box">
                      <div className="search-box position-relative" style={{ width: '16rem' }}>
                        <Input
                          type="text"
                          value={inputValue}
                          onChange={handleInputChange}
                          className="form-control rounded border"
                          placeholder={"Search by Requisition Number"}

                        />
                        <i className="mdi mdi-magnify search-icon"></i>
                      </div>
                    </div>
                  </div>
                  <RequireUserType userType={USER_TYPES_ENUM.CLIENT}>
                    <Button variant="contained" sx={{ backgroundColor: '#0053FF', ml: 2 }}
                      onClick={() => navigate("/create-requisition")}
                    >
                      <Add sx={{ mr: 1 }} /> {"Add PR"}
                    </Button>
                  </RequireUserType>
                </div>
                <div
                  className="ag-theme-quartz"
                  style={{
                    height: '550px',
                    width: '100%'
                  }}
                >
                  <AgGridReact
                    ref={gridRef}
                    suppressRowClickSelection={true}
                    columnDefs={columnDefs}
                    pagination={true}
                    paginationPageSize={paginationPageSize}
                    paginationPageSizeSelector={paginationPageSizeSelector}
                    autoSizeStrategy={autoSizeStrategy}
                    rowData={rowData}
                    reactiveCustomComponents
                    onPaginationChanged={onPaginationChanged}
                    onSortChanged={handleSortChange}
                    sortingOrder={["desc", "asc"]}
                  >
                  </AgGridReact>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  )
}

export default connect(null, { setBreadcrumbItems })(AllRequisition);
